import { Component } from 'react';
import { Navigate } from 'react-router-dom';

import { 
  Typography,
  FormControl,
  TextField, 
  Button,
} from '@mui/material';
import { 
  ThemeProvider, 
  createTheme, 
} from '@mui/material/styles';
import { 
  VisibilityOff, 
  Visibility,
  OpenInNew, 
  HighlightOff 
} from '@mui/icons-material';

// import { HttpStatusCode } from 'axios';

import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

import { Trans, withTranslation } from 'react-i18next';
import i18n from '../assets/locales';

import '../assets/css/signup.css'

import Layout from '../layouts/layout.component';

import UserService from '../services/user.service';


type Props = {  
};

type State = {
  redirect: string | null,
  username: string,
  email: string,
  password: string | null,
  showPassword: boolean,
  // confirmPassword: string | null,
  // showConfirmPassword: boolean,
  agreeToTerms: boolean,
  agreeToCollectInfo: boolean,
  joinMailingList: boolean,
  loading: boolean,
  success: boolean,
  message: string,
};

const theme = createTheme({
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontSize: 13,
          fontWeight: 'bold',
          color: 'rgba(224, 224, 224, 0.98)' ,
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          // Standard
          '& .MuiInput-root': {
            color: 'rgba(255, 255, 255, 0.8)',
            // fontFamily: 'Arial',
            fontWeight: 'bold',
            '&:before': {
              borderColor: 'rgba(128, 128, 128, 0.8)',
              borderWidth: '1px',
            },
            '&:after': {
              borderColor: 'rgba(0, 98, 65, 0.8)',
              borderWidth: '2px',
            },
            ':hover:not(.Mui-focused)': {
              '&:before': {
                borderColor: 'rgba(231, 231, 231, 0.8)',
                borderWidth: '1px',
              },
            },
          },
        }
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.8)',
        }
      }
    }
  },
});


class Register extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.handleShowPassword = this.handleShowPassword.bind(this);
    this.handleSignin = this.handleSignin.bind(this);
    this.handleSignup = this.handleSignup.bind(this);

    this.state = {
      redirect: null,
      username: '',
      email: '',
      password: null,
      showPassword: false,
      // confirmPassword: null,
      agreeToTerms: false,
      agreeToCollectInfo: false,
      joinMailingList: false,
      message: '',
      loading: false,
      success: false,
    };
  }

  componentDidMount() {
    // const user = AuthService.getCurrentUser();

    // if (user) {
    //   this.setState({ redirect: '/profile' });
    // };
  }

  componentWillUnmount() {
    window.location.reload();
  }

  validationSchema() {
    const validity = Yup.object().shape({
      username: Yup.string()
        .required('Username is required!')
        .min(4, 'Username must be at least 4 characters')
        .max(12, 'Username must be at most 12 characters')
        .matches(/^[^.]*$/, {
          message: 'No period'
        })
        .matches(/^[^!@#$%^&*+=<>:;|~]*$/, {
          message: 'No symbols'
        })
        .matches(/^[a-zA-Z0-9]+$/, {  //original matcher
          message: 'Invalid format.',
        }),
      email: Yup.string()
        .required('Email is required!')
        .email('Enter a valid email.'),
      password: Yup.string()
        .required('Password is required!')
        .test(
          'len',
          'The password must be between 8 and 20 characters.',
          (val: any) => val && val.toString().length >= 8 && val.toString().length <= 20
        ),
      // confirm_password: Yup.string()
      //   .required('Confirm Password is required!')
      //   .oneOf([Yup.ref('password'), ''], 'Passwords must match')
      //   .test(
      //     'len',
      //     'The confirm password must be between 8 and 20 characters.',
      //     (val: any) => val && val.toString().length >= 8 && val.toString().length <= 20
      //   ),
      agreeToTerms: Yup.boolean()
        .required('You must agree to the terms and conditions.')
        .test(
          'is-true', 
          'Please agree to the terms and conditions.', 
          (val) => val && val === true
        ),
    });

    // console.log(`validationSchema: ${JSON.stringify(validity, null, 2)}`);

    return validity;
  }

  handleShowPassword() {
    this.setState((prevState) => ({
      showPassword: !prevState.showPassword
    }));
  }

  handleSignin() {
    window.location.href = '/';
  }

  handleSignup(values: { username: string; email: string; password: string }) {
    const { username, email, password } = values;

    console.log(`onSubmit:: username: ${username}, email: ${email}, password: ${password}`);

    this.setState({
      message: '',
      success: false,
      loading: true,
    });

    UserService.register(
      username,
      email,
      password
    ).then(
      async (res) => {
        console.log(`register -> ${res.status} ${res.statusText}`);
        this.setState({
          loading: false,
          success: true,
          message: res.data.message,
          redirect: '/account/register-requirement'
        });
      },
      (err) => {
        const message =
          (err.response &&
            err.response.data &&
            err.response.data.message) ||
          err.message ||
          err.toString();

        this.setState({
          loading: false,
          success: false,
          message: message
        });
      }
    );
  }

  render() {
    if (this.state.redirect) {
      return <Navigate to={this.state.redirect} />
    }

    const {
      loading,
      success,
      showPassword,
      message
    } = this.state;

    const initialValues = {
      username: '',
      email: '',
      showPassword: false,
      password: '',
      // confirmPassword: '',
      joinMailingList: false,
      agreeToTerms: false,
    };

    return (
      <Layout>
        <div className='container col-md-12'>
          <div className='divider-10' />
          <div className='container-fluid'>
            <Typography variant='h5'><b>{ i18n.t('signup:createAccount') }</b></Typography>
          </div>
          <div className='divider-10' />
          <div className='container card card-container' style={{background: 'transparent'}}>
            {/* <div sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div>
                <Typography variant='caption'><sup style={{ color: 'rgb(224, 0, 0, 1)' }}>*</sup> indicates required field</Typography>
              </div>
            </div> */}
            <div className='container m-2' />
            <ThemeProvider theme={theme}>
              <Formik initialValues={initialValues}
                      validationSchema={this.validationSchema}
                      onSubmit={this.handleSignup}>
                <Form>
                  <FormControl fullWidth>
                    <Typography variant='subtitle1'>
                      {i18n.t('signup:username')} <sup style={{color:'rgb(224, 0, 0, 1)'}}>*</sup>
                    </Typography>
                    <Field
                      as={TextField}
                      name='username'
                      type='text'
                      id='username'
                      variant='standard'
                      autoComplete='off'
                      />
                    <ErrorMessage name='username'>
                      {(msg) => <div style={{fontSize:13, color:'rgba(224, 0, 0, 1)', textAlign:'left'}}>{msg}</div>}
                    </ErrorMessage>
                  </FormControl>
                  <div className='divider-10' />
                  <FormControl fullWidth>
                    <Typography variant='subtitle1'>
                      {i18n.t('signup:email')}<sup style={{color:'rgb(224, 0, 0, 1)'}}>*</sup>
                    </Typography>
                    <Field
                      as={TextField}
                      name='email'
                      type='text'
                      id='email'
                      variant='standard'
                      autoComplete='off'
                      />
                    <ErrorMessage name='email'>
                      {(msg) => <div style={{fontSize:13, color:'rgba(224, 0, 0, 1)', textAlign:'left'}}>{msg}</div>}
                    </ErrorMessage>
                  </FormControl>
                  <div className='divider-10' />
                  <FormControl fullWidth>
                    <Typography variant='subtitle1'>
                      {i18n.t('signup:password')} <sup style={{color:'rgb(224, 0, 0, 1)'}}>*</sup>
                    </Typography>
                    <Field 
                      as={TextField}
                      name='password'
                      type={showPassword?'text':'password'}
                      id='password'
                      autoComplete='new-password'
                      variant='standard'
                      InputProps={{
                        endAdornment: (
                          <Button onClick={this.handleShowPassword} size='small'>
                            {showPassword?<Visibility />:<VisibilityOff />}
                          </Button>
                        )
                      }}/>
                    <ErrorMessage name='password'>
                      {(msg) => <div style={{fontSize: 13, color: 'rgba(224, 0, 0, 1)', textAlign: 'left'}}>{msg}</div>}
                    </ErrorMessage>
                  </FormControl>
                  {/* <div className='divider-10' />
                  <FormControl fullWidth>
                    <Typography variant='subtitle1'>
                      Confirm Password <sup style={{ color: 'rgb(224, 0, 0, 1)' }}>*</sup>
                    </Typography>
                    <Field name='confirm_password'
                           type='password'
                           as={TextField}
                           id='confirm_password'
                           autoComplete='new-password'
                           variant='standard'/>
                    <ErrorMessage name='confirm_password'>
                      {(msg) => <div style={{fontSize: 13, color: 'rgba(224, 0, 0, 1)', textAlign: 'left'}}>{msg}</div>}
                    </ErrorMessage>
                  </FormControl> */}
                  <div className='divider-10' />
                  <FormControl fullWidth>
                  <div>
                    <Field 
                      name='agreeToTerms'
                      type='checkbox'
                      id='agreeToTerms'
                      label='Agree to Terms and Conditions'
                      variant='standard'
                      />
                    <sup style={{color:'rgb(224, 0, 0, 1)'}}>*</sup>
                    <span className='text-description'>
                      <Trans 
                        i18nKey='signup:agreeToTerms'
                        components={{
                          linkToTerms: <a href='/terms' target='_blank' rel='noopener noreferrer'>Terms<OpenInNew fontSize='small' /></a>,
                          linkToPrivacyPolicy: <a href='/privacy' target='_blank' rel='noopener noreferrer'>Privacy</a>,
                          openInNew: <OpenInNew />
                        }}
                        />
                    </span>
                  </div>
                  <ErrorMessage name='agreeToTerms'>
                    {(msg) => <div style={{fontSize:13, color:'rgba(224, 0, 0, 1)', textAlign:'left'}}><HighlightOff fontSize='small' /> {msg}</div>}
                  </ErrorMessage>
                  </FormControl>
                  <div className='divider-10' />
                  <div>
                    <Field 
                      name='agreeToCollectInfo'
                      type='checkbox'
                      id='agreeToCollectInfo'
                      label='Agree to Collect Infomation'
                      variant='standard'
                      />
                      <span className='text-description'>
                        {i18n.t('signup:agreeToCollectInfo')}
                      </span>
                  </div>
                  <div className='divider-10' />
                  <div>
                    <Field 
                      name='joinMailingList'
                      type='checkbox'
                      id='joinMailingList'
                      label='Join our mailing list'
                      variant='standard'
                      />
                      <span className='text-description'>
                        {i18n.t('signup:joinMailingList')}
                      </span>
                  </div>
                  <div className='divider-20' />
                  <div className='row'>
                    <div className='col'>
                    </div>
                    <div className='col'>
                      <Button 
                        className='btn btn-custom btn-sm btn-block w-80'
                        onClick={this.handleSignin}>
                        <span>{i18n.t('signup:signin')}</span>
                      </Button>
                    </div>
                    <div className='col'>
                      <Button 
                        className="btn btn-custom btn-sm btn-block w-80" 
                        type='submit'
                        disabled={loading}
                        style={{border: '1px solid rgb(0, 96, 0)'}}>
                        {loading && (<span className="spinner-border spinner-border-sm mr=3"></span> )}
                        <span>{i18n.t('signup:signup')}</span>
                      </Button>
                    </div>
                  </div>
                  {message && (
                    <>
                      <div className='divider-20' />
                      <div className="form-group">
                        <div className="alert alert-danger" role="alert">
                          { message }
                        </div>
                      </div>
                    </>
                  )}
                </Form>
              </Formik>
            </ThemeProvider>
          </div>
        </div>
      </Layout>
    );
  }
}

export default withTranslation()(Register)
